import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { brand } from "@/mocks/site";
import { submissionMaterials } from "@/mocks/guide";
import SubmissionForm from "./components/SubmissionForm";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "我要投稿 - 提交爆料线索",
    description: "通过在线表单提交爆料投稿线索，支持匿名与化名，编辑工作日 24 小时内给出初审答复。",
    url: "https://sxnxp.cn/submit",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: brand.name,
    url: "https://sxnxp.cn/",
    email: brand.email,
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://sxnxp.cn/" },
      { "@type": "ListItem", position: 2, name: "我要投稿", item: "https://sxnxp.cn/submit" },
    ],
  },
];

const afterSubmit = [
  { icon: "ri-inbox-archive-line", title: "线索入库", desc: "提交后进入初审队列，只有对接编辑可以查看。" },
  { icon: "ri-search-eye-line", title: "真实性初审", desc: "编辑核对关键细节，必要时联系你补充一处材料。" },
  { icon: "ri-chat-check-line", title: "沟通跟进方式", desc: "确认授权范围、署名方式与是否配合补充采访。" },
  { icon: "ri-article-line", title: "进入编辑流程", desc: "合适的选题进入核实与写作，发布前会与你同步。" },
];

export default function Submit() {
  useSeo({
    title: "我要投稿 - 在线提交爆料线索 | 爆点投稿社",
    description:
      "填写在线投稿表单，提交你掌握的爆料线索。支持匿名与化名，附投稿材料清单与保护说明，编辑工作日 24 小时内给出初审答复。",
    keywords: "爆料投稿,在线投稿,投稿表单,提供线索,匿名投稿",
    path: "/submit",
    jsonLd,
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100 px-4 pb-10 pt-8 md:px-6 md:pb-14 md:pt-10">
        <div className="mx-auto w-full">
          <Breadcrumbs items={[{ label: "我要投稿" }]} />
          <div className="mt-6 max-w-3xl">
            <p className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] uppercase text-primary-600">
              <span className="h-px w-6 bg-primary-400"></span>
              立即投稿
            </p>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              把你手上的线索，交给对的人
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              不需要文采，也不需要等材料十全十美。先把你亲眼所见、亲身经历的事讲清楚，
              编辑会判断它值不值得跟进，并告诉你还缺什么。
            </p>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 px-4 py-12 md:px-6 md:py-16">
        <div className="mx-auto grid w-full grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <SubmissionForm />
          </div>

          <aside className="space-y-6 lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-file-text-line text-lg leading-none text-primary-500"></i>
                投稿须知
              </h2>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-base leading-none text-secondary-500"></i>
                  只提交你确认或愿意核实的真实信息，避免转述道听途说。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-base leading-none text-secondary-500"></i>
                  请勿在公开平台提前发布原始材料，避免影响后续核实。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-base leading-none text-secondary-500"></i>
                  涉及未成年人或受害者的信息，请主动说明，编辑会处理后再使用。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-base leading-none text-secondary-500"></i>
                  一位投稿人可多次提交，但建议一次集中写清一条线索。
                </li>
              </ul>
            </div>

            <div className="rounded-lg bg-secondary-900 p-6 text-background-50">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-background-50">
                <i className="ri-shield-user-line text-lg leading-none text-accent-400"></i>
                关于你的安全
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-background-100/75">
                投稿信息全程加密保存，只有对接编辑可见。你可以选择完全匿名或使用化名；
                涉及敏感内容时，我们会先与你确认保护方案，再决定是否推进选题。
              </p>
              <p className="mt-4 flex items-center gap-2 text-sm text-accent-400">
                <i className="ri-mail-line text-base leading-none"></i>
                {brand.email}
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-stack-line text-lg leading-none text-primary-500"></i>
                建议准备的材料
              </h2>
              <ul className="mt-4 space-y-3.5">
                {submissionMaterials.map((material) => (
                  <li key={material.title} className="flex gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                      <i className={`${material.icon} text-base leading-none`}></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{material.title}</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-foreground-500">{material.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full bg-background-100 px-4 py-16 md:px-6 md:py-20">
        <div className="mx-auto w-full">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] uppercase text-primary-600">
              <span className="h-px w-6 bg-primary-400"></span>
              投稿之后
            </p>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              提交之后，你的线索会经历这四步
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              全过程你可以随时询问进展。如果需要撤回线索，也可以直接邮件告知编辑。
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {afterSubmit.map((item, index) => (
              <article key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${item.icon} text-xl leading-none`}></i>
                  </span>
                  <span className="font-heading text-2xl font-bold text-background-300">
                    0{index + 1}
                  </span>
                </div>
                <h3 className="mt-5 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </article>
            ))}
          </div>

          <div className="mt-10 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm leading-relaxed text-foreground-600">
              还不确定怎么写？先看看投稿指南里的标题技巧与自查清单。
            </p>
            <Link
              to="/guide"
              className="inline-flex w-fit items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap cursor-pointer"
            >
              阅读投稿指南
              <i className="ri-arrow-right-line text-base leading-none"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}